import { useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Flex,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import {
  AssetConfigRail,
  type IAssetConfigRailItem,
} from '@/layouts/AssetWorkspaceLayout';
import {
  getBrowserPanelState,
  registerBrowserPanelHost,
  subscribeToBrowserPanel,
} from '@/modules/outputs/browser-panel-registry';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import type { TResolvedFile } from '@/modules/outputs/types';
import { useConfigRailExpanded } from '@/shared/hooks/use-rail-preferences';
import { selectActiveTourId, useTourStore } from '@/shared/tours';

import {
  AutomationRailActions,
  type IAutomationRailActionsProps,
} from './AutomationRailActions';
import type { IDraftEditorRightRailProps } from './DraftEditorRightRail';
import { RailBrowserSection } from './RailBrowserSection';
import { RailIntegrationsSection } from './RailIntegrationsSection';
import { RailLibrarySection } from './RailLibrarySection';
import { RailTriggersSection } from './RailTriggersSection';
import { RailTweaksSection } from './RailTweaksSection';
import { SelectedStepPanel } from './SelectedStepPanel';
import { useRailIntegrations } from './use-rail-integrations';
import { useRailLibrary } from './use-rail-library';
import { useRailTriggers } from './use-rail-triggers';

/** Stable identity so the default doesn't re-run the schedule check each render. */
const EMPTY_DEFAULT_INPUT_KEYS: string[] = [];

/**
 * Props for {@link AutomationConfigRail}. The draft rail's data, with Run and
 * Publish described by `actions` rather than spread across props; Test is a
 * document tab in the asset workspace.
 */
export interface IAutomationConfigRailProps
  extends Omit<
    IDraftEditorRightRailProps,
    | 'onRun'
    | 'onTest'
    | 'canRun'
    | 'canTest'
    | 'isRunning'
    | 'isTesting'
    | 'primaryAction'
  > {
  /** Run and Publish at the top of the rail, beside its toggle. */
  actions?: Omit<IAutomationRailActionsProps, 'automationId' | 'isCollapsed'>;
}

/**
 * The draft's configuration on the shared `AssetConfigRail`. Collapsed, it
 * shows Triggers, Integrations, Tweaks and Library as icons with counts;
 * expanded, the same sections the draft rail shows, or the selected step.
 */
export function AutomationConfigRail({
  automationId,
  automationName,
  manifest,
  inputSpecs,
  automationConfig,
  onSaveConfigValue,
  credentialsByAlias,
  selectedStep,
  selectedStepStatus,
  selectedStepIO,
  selectedStepRunningCalls,
  defaultInputKeys = EMPTY_DEFAULT_INPUT_KEYS,
  onTestStep,
  actions,
  onAddIntegration,
}: IAutomationConfigRailProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');
  const { t: tBrowser } = useTranslation('browser-automation');
  // Starts collapsed: the strip's counts say what's configured at a glance.
  const [isExpandedPreference, setIsExpanded] = useConfigRailExpanded();
  // Tours anchor on the sections (the draft-creation tour points at the
  // Triggers "+"), which a collapsed rail does not render.
  const activeTourId = useTourStore(selectActiveTourId);
  const isExpanded = isExpandedPreference || Boolean(activeTourId);

  // Hosts sessions even while collapsed, so none falls back into the chat.
  // eslint-disable-next-line no-restricted-syntax -- reason: registers in a module registry, with cleanup
  useEffect(() => registerBrowserPanelHost(), []);
  const { sessionIds: browserSessionIds, focusRequest } = useSyncExternalStore(
    subscribeToBrowserPanel,
    getBrowserPanelState,
  );
  // Only Opens made after mount expand the rail; the registry outlives the page.
  const [initialFocusRequest] = useState(focusRequest);
  // eslint-disable-next-line no-restricted-syntax -- reason: reacts to an explicit focus request raised outside React
  useEffect(() => {
    if (focusRequest !== initialFocusRequest) setIsExpanded(true);
  }, [focusRequest, initialFocusRequest, setIsExpanded]);

  // Not in the sections: they unmount while a step is selected, so they'd refetch.
  const triggers = useRailTriggers(automationId, inputSpecs, defaultInputKeys);
  const integrations = useRailIntegrations(credentialsByAlias);
  const library = useRailLibrary(manifest);
  const [previewFile, setPreviewFile] = useState<TResolvedFile | undefined>();

  const items: IAssetConfigRailItem[] = [
    {
      id: 'triggers',
      icon: 'Zap',
      label: tAutomation('automation.draftRail.triggers'),
      count: triggers.triggerCount,
    },
    {
      id: 'integrations',
      icon: 'Plug',
      label: tAutomation('automation.draftRail.integrations'),
      count: integrations.connections.length,
    },
    {
      id: 'tweaks',
      icon: 'Settings2',
      label: tAutomation('automation.draftRail.tweaks'),
      count: Object.keys(automationConfig ?? {}).length,
    },
    {
      id: 'library',
      icon: 'FileText',
      label: tAutomation('automation.draftRail.library'),
      count: library.assetUris.length + library.collectionSlugs.length,
    },
  ];
  // First, matching the expanded rail, where the live session sits on top.
  if (browserSessionIds.length > 0) {
    items.unshift({
      id: 'browser',
      icon: 'Globe',
      label: tBrowser('browserAutomation.liveView.title'),
      count: browserSessionIds.length,
    });
  }

  const selectedStepLabel = tAutomation(
    'automation.draftRail.stepSelectedExpand',
    { number: selectedStep?.number },
  );

  return (
    <AssetConfigRail
      items={items}
      isExpanded={isExpanded}
      onExpandedChange={setIsExpanded}
      label={tCommon('common.assetWorkspace.configuration')}
      actions={
        actions && (
          <AutomationRailActions
            {...actions}
            automationId={automationId}
            isCollapsed={!isExpanded}
          />
        )
      }
      collapsedExtra={
        selectedStep?.number !== undefined && (
          <Tooltip delayDuration={200} variant="primary">
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                onClick={() => setIsExpanded(true)}
                className="draft-selection-ring shrink-0 rounded-full border-2 bg-card text-xs font-medium tabular-nums text-foreground"
                aria-label={selectedStepLabel}
                data-testid="draft-rail-collapsed-selected-step"
              >
                {selectedStep.number}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">{selectedStepLabel}</TooltipContent>
          </Tooltip>
        )
      }
      overlays={
        // Outside the sections so a preview opened from the Library survives selecting a step.
        <PreviewLayover
          isOpen={previewFile !== undefined}
          outputName=""
          outputValue={previewFile ?? null}
          onClose={() => setPreviewFile(undefined)}
          showCloseButton
        />
      }
      data-testid="automation-config-rail"
    >
      {/* Outside the step switch so the stream survives selecting a step. */}
      <RailBrowserSection />
      {selectedStep ? (
        <SelectedStepPanel
          step={selectedStep}
          automationConfig={automationConfig}
          onSaveConfigValue={onSaveConfigValue}
          credentialsByAlias={credentialsByAlias}
          status={selectedStepStatus}
          io={selectedStepIO}
          runningCalls={selectedStepRunningCalls}
          onTestStep={onTestStep}
        />
      ) : (
        <>
          <RailTriggersSection
            automationId={automationId}
            automationName={automationName}
            inputSpecs={inputSpecs}
            triggers={triggers}
          />
          <RailIntegrationsSection
            automationId={automationId}
            integrations={integrations}
            onAddIntegration={onAddIntegration}
          />
          <RailTweaksSection
            automationConfig={automationConfig}
            onSaveConfigValue={onSaveConfigValue}
          />
          <RailLibrarySection
            library={library}
            onPreviewFile={setPreviewFile}
          />
        </>
      )}
      {!selectedStep && (manifest.steps?.length ?? 0) > 0 && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap={2}
          className="min-h-36 flex-1 px-8 text-center"
          data-testid="draft-rail-step-empty-state"
        >
          <Icon
            type="MousePointerClick"
            size="md"
            className="text-muted-foreground"
          />
          <Text level="xSmall" color="muted">
            {tAutomation('automation.draftRail.selectStep')}
          </Text>
        </Flex>
      )}
    </AssetConfigRail>
  );
}
